import React from "react";
import { motion } from "framer-motion";
import {
	BarChart3,
	TrendingUp,
	AlertCircle,
	CheckCircle2,
} from "lucide-react";

export { default as MarketCompetitionSection } from "./MarketCompetitionSection";
export { default as SentimentAnalysisSection } from "./SentimentAnalysisSection";

export function AnalysisSummarySection({ report }) {
	if (!report?.providers?.length) return null;

	const consensus = report.consensus || {};

	return (
		<motion.div
			className="report-section"
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
		>
			<div className="report-section__header">
				<div className="report-section__icon-wrap report-section__icon-wrap--indigo">
					<BarChart3 className="report-section__icon" />
				</div>
				<div className="report-section__header-text">
					<h2 className="report-section__title">Analysis Summary</h2>
					<p className="report-section__subtitle">
						Cross-provider consensus and key insights
					</p>
				</div>
			</div>

			<div className="report-section__grid">
				{report.providers.map((provider) => (
					<div key={provider.provider} className="report-section__provider-card">
						<h3 className="report-section__provider-name">{provider.provider}</h3>

						{provider.strengths?.length > 0 && (
							<div className="mb-2">
								<p className="report-section__list-label report-section__list-label--success">
									Strengths
								</p>
								<ul className="report-section__bullet-list">
									{provider.strengths.map((item, i) => (
										<li key={i} className="report-section__bullet-item">
											<CheckCircle2 className="report-section__bullet-icon report-section__status-icon--ok" />
											<span>{item}</span>
										</li>
									))}
								</ul>
							</div>
						)}

						{provider.weaknesses?.length > 0 && (
							<div className="mb-2">
								<p className="report-section__list-label report-section__list-label--warning">
									Weaknesses
								</p>
								<ul className="report-section__bullet-list">
									{provider.weaknesses.map((item, i) => (
										<li key={i} className="report-section__bullet-item">
											<AlertCircle className="report-section__bullet-icon report-section__status-icon--fail" />
											<span>{item}</span>
										</li>
									))}
								</ul>
							</div>
						)}

						{provider.key_blocker && (
							<div className="report-section__divider">
								<p className="report-section__list-label report-section__list-label--muted">
									Key Blocker
								</p>
								<p className="report-section__summary-text">{provider.key_blocker}</p>
							</div>
						)}

						{provider.key_opportunity && (
							<div className="mt-2">
								<p className="report-section__list-label report-section__list-label--muted">
									Key Opportunity
								</p>
								<p className="report-section__summary-text">{provider.key_opportunity}</p>
							</div>
						)}
					</div>
				))}
			</div>

			<div className="report-section__callout report-section__callout--indigo">
				<h3 className="report-section__callout-title">
					<TrendingUp className="report-section__callout-icon" style={{ color: "#4f46e5" }} />
					Multi-Model Consensus
				</h3>

				<div className="report-section__insights-grid">
					{consensus.shared_strengths?.length > 0 && (
						<div>
							<p className="report-section__list-label report-section__list-label--success">
								Shared Strengths
							</p>
							<ul className="report-section__numbered-list">
								{consensus.shared_strengths.map((item, i) => (
									<li key={i}>• {item}</li>
								))}
							</ul>
						</div>
					)}

					{consensus.shared_weaknesses?.length > 0 && (
						<div>
							<p className="report-section__list-label report-section__list-label--warning">
								Shared Weaknesses
							</p>
							<ul className="report-section__numbered-list">
								{consensus.shared_weaknesses.map((item, i) => (
									<li key={i}>• {item}</li>
								))}
							</ul>
						</div>
					)}
				</div>

				{consensus.final_summary && (
					<div className="report-section__divider">
						<p className="report-section__callout-text">{consensus.final_summary}</p>
					</div>
				)}

				{consensus.common_priority_fixes?.length > 0 && (
					<div className="mt-2">
						<p className="report-section__list-label" style={{ color: "#4338ca" }}>
							Priority Actions
						</p>
						<ul className="report-section__numbered-list">
							{consensus.common_priority_fixes.map((item, i) => (
								<li key={i} className="report-section__bullet-item">
									<span style={{ fontWeight: 700, color: "#4338ca" }}>{i + 1}.</span>
									<span>{item}</span>
								</li>
							))}
						</ul>
					</div>
				)}
			</div>
		</motion.div>
	);
}
